import LoginRequest from '../models/LoginRequest';
import LoginResponse from '../models/LoginResponse';
import Patient from '../models/Patient';
import Translation from '../models/Translation';
import User from '../models/User';

//#region API Common
export const host = process.env.REACT_APP_API_HOST;
export const api = host + 'api/v1/';
export const requestHeaders = { Accept: '*/*', 'Content-Type': 'application/json' };
//#endregion

const error400 = (response: Response) =>
    response.status === 400 ? `400/${response.headers.get('X-Error-Code') ?? '000'}` : '';

//#region Authentication

export const authenticate = async (loginRequest: LoginRequest) => {
    const body = JSON.stringify({
        username: loginRequest.username,
        password: loginRequest.password,
    });

    const response = await fetch(`${api}login/authenticate`, {
        method: 'POST',
        headers: requestHeaders,
        body,
        credentials: "include",
    });

    if (response.status !== 200) return null;
    const loginResponse = (await response.json()) as LoginResponse;

    return loginResponse;

};

export const logoutApi = async () => {
  await fetch(`${api}login/logout`, {
    method: 'POST',
    credentials: 'include'
  });
};

export const getCurrentUser = async () => {
  const response = await fetch(`${api}login/me`, {
    method: 'GET',
    credentials: 'include'
  });

  if (response.status !== 200) return null;
  return await response.json();
};
//#endregion

//#region Standard API calls (CRUD)

export async function getAll<T extends Patient | User | Translation>(endpoint: string) {

    const headers = { ...requestHeaders };
    const response = await fetch(api + endpoint, { method: 'GET', headers, credentials: "include" });

    const data: T[] = response.status === 200 ? await response.json() : [];
    const error = error400(response);

    return { status: response.status, data, error };
}

export async function getOne<T extends Patient | User>(endpoint: string, id: string) {

    const headers = { ...requestHeaders };
    const response = await fetch(api + endpoint + id, { method: 'GET', headers, credentials: "include" });

    const data: T | null = response.status === 200 ? await response.json() : null;
    const error = error400(response);

    return { status: response.status, data, error };
}

export async function addOne<T extends Patient | User>(endpoint: string, element: T) {

    const headers = { ...requestHeaders };
    const response = await fetch(api + endpoint, {
        method: 'POST',
        body: JSON.stringify(element),
        headers,
        credentials: "include",
    });

    const data: T | null = response.status === 201 ? await response.json() : null;
    const error = error400(response);

    return { status: response.status, data, error };
}

export async function updateOne<T extends Patient | User>(endpoint: string, element: T) {

    const headers = { ...requestHeaders };
    const response = await fetch(api + endpoint + element.id, {
        method: 'PUT',
        body: JSON.stringify(element),
        headers,
        credentials: "include",
    });

    const data: T | null = response.status === 204 ? element : null;
    const error = error400(response);

    return { status: response.status, data, error };
}

export async function updateOnePatient<T extends Patient | User>(
    endpoint: string,
    element: T,
    userId: string | null
) {

    const headers = { ...requestHeaders };
    const response = await fetch(api + endpoint + element.id + '/' + userId, {
        method: 'PUT',
        body: JSON.stringify(element),
        headers,
        credentials: "include",
    });

    const data: T | null = response.status === 204 ? element : null;
    const error = error400(response);

    return { status: response.status, data, error };
}

export async function deleteOne(endpoint: string, id: string) {

    const headers = { ...requestHeaders };
    const response = await fetch(api + endpoint + id, { method: 'DELETE', headers, credentials: "include" });

    const error = error400(response);

    return { status: response.status, success: response.status === 204, error };
}

//#endregion

//#region Special API calls

export async function uploadFile(file: File) {

    const formData = new FormData();
    formData.append('file', file, file.name);

    const endpoint = 'files/upload';
    const headers = { Accept: '*/*' };
    const response = await fetch(api + endpoint, { method: 'POST', body: formData, headers, credentials: "include" });

    const data: string | null = response.status === 200 ? (await response.json()).fileName : null;
    const error = error400(response);

    return { status: response.status, data, error };
}

export async function getPdfReport(patientId: string) {

    const endpoint = 'files/b71f97b1-62b4-47a1-a397-09f3c8c71626/';
    const headers = { ...requestHeaders };
    const response = await fetch(api + endpoint + patientId, { method: 'GET', headers, credentials: "include" });

    const data = response.status === 200 ? await response.blob() : null;
    const error = error400(response);

    return { status: response.status, data, error };
}

export async function changePassword(id: string, oldPassword: string, newPassword: string) {

    const headers = { ...requestHeaders };
    const response = await fetch(`${api}users/changepassword/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ oldPassword, newPassword }),
        headers,
        credentials: "include",
    });

    const error = error400(response);

    return { status: response.status, success: response.status === 204, error };
}

export async function getSoftwareVersion() {
    const response = await fetch(`${api}version`);
    return response.status === 200 ? (await response.text()).replaceAll('"', '') : 'unknown';
}

export async function getVersionUDI() {
    const response = await fetch(`${api}versionUDI`);
    return response.status === 200 ? (await response.text()).replaceAll('"', '') : 'unknown';
}

export async function getAppIsOnMaintenance() {
    const response = await fetch(`${api}isOnMaintenance`);
    return response.status === 200 ? (await response) : true;
}

//#endregion
